import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DollarSign, TrendingUp, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { feeCollectionAPI } from '../services/api';
import { DEFAULT_REPORT_MONTH, DEFAULT_REPORT_YEAR } from '../utils/reportPeriod';

const FeeChargeCards = ({ month, year }) => {
  const { t } = useTranslation();
  const [feeData, setFeeData] = useState({
    totalEstimate: 0,
    totalCollected: 0,
    remaining: 0,
    collectionRate: 0,
    growthRate: 0
  });
  const [weeklyData, setWeeklyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const monthParam = month != null ? month : DEFAULT_REPORT_MONTH;
  const yearParam = year != null ? year : DEFAULT_REPORT_YEAR;

  useEffect(() => {
    fetchFeeCollectionData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when period changes
  }, [monthParam, yearParam]);

  const fetchFeeCollectionData = async () => {
    try {
      setLoading(true);
      // API uses 0-based month in some backends; we pass 1-12, adjust if needed
      const apiMonth = monthParam >= 1 && monthParam <= 12 ? monthParam : new Date().getMonth();

      const response = await feeCollectionAPI.getOverview({
        month: apiMonth,
        year: yearParam
      });

      if (response.success) {
        const { overview, weeklyBreakdown } = response.data;
        setFeeData({
          totalEstimate: overview.totalEstimate,
          totalCollected: overview.totalCollected,
          remaining: overview.remaining,
          collectionRate: overview.collectionRate,
          growthRate: overview.growthRate
        });
        setWeeklyData(weeklyBreakdown);
      }
    } catch (err) {
      console.error('Error fetching fee collection data:', err);
      setError(t('feeChargeCards.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount >= 1000000000) {
      return `${(amount / 1000000000).toFixed(1)}B ₭`;
    } else if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(1)}M ₭`;
    }
    return new Intl.NumberFormat('lo-LA').format(amount) + ' ₭';
  };

  const formatNumber = (num) => {
    return num.toLocaleString();
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t('feeChargeCards.title')}</h2>
          <p className="text-gray-600">{t('feeChargeCards.subtitle')}</p>
        </div>
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mr-3" />
          <span className="text-gray-600">{t('feeChargeCards.loading')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t('feeChargeCards.title')}</h2>
          <p className="text-gray-600">{t('feeChargeCards.subtitle')}</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <AlertCircle className="h-6 w-6 text-red-600 mr-3" />
            <div>
              <h3 className="text-lg font-semibold text-red-800">{t('common.errorLoadingData')}</h3>
              <p className="text-red-600 mt-1">{error}</p>
              <button
                onClick={fetchFeeCollectionData}
                className="mt-3 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                {t('common.tryAgain')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const cards = [
    {
      title: t('feeChargeCards.totalEstimate'),
      value: feeData.totalEstimate,
      icon: DollarSign,
      iconColor: 'text-blue-500',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      description: t('feeChargeCards.estimatedTotalFees'),
      change: feeData.growthRate > 0 ? `+${feeData.growthRate.toFixed(1)}%` : `${feeData.growthRate.toFixed(1)}%`,
      changeColor: feeData.growthRate >= 0 ? 'text-green-600' : 'text-red-600'
    },
    {
      title: t('feeChargeCards.totalCollected'),
      value: feeData.totalCollected,
      icon: CheckCircle,
      iconColor: 'text-green-500',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      description: t('feeChargeCards.collectionRate', { value: feeData.collectionRate }),
      change: feeData.growthRate > 0 ? `+${(feeData.growthRate * 0.8).toFixed(1)}%` : `${(feeData.growthRate * 0.8).toFixed(1)}%`,
      changeColor: feeData.growthRate >= 0 ? 'text-green-600' : 'text-red-600'
    },
    {
      title: t('feeChargeCards.remaining'),
      value: feeData.remaining,
      icon: AlertCircle,
      iconColor: 'text-orange-500',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      description: t('feeChargeCards.uncollected', { value: (100 - feeData.collectionRate).toFixed(1) }),
      change: feeData.growthRate > 0 ? `-${(feeData.growthRate * 0.5).toFixed(1)}%` : `+${Math.abs(feeData.growthRate * 0.5).toFixed(1)}%`,
      changeColor: feeData.growthRate >= 0 ? 'text-green-600' : 'text-red-600'
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">{t('feeChargeCards.title')}</h2>
        <p className="text-gray-600">{t('feeChargeCards.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {cards.map((card, index) => {
          const Icon = card.icon;
          return (
            <div
              key={card.title}
              className={`${card.bgColor} ${card.borderColor} border-2 rounded-lg p-6 hover:shadow-md transition-shadow duration-200`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`p-2 rounded-lg bg-white shadow-sm`}>
                  <Icon className={`h-6 w-6 ${card.iconColor}`} />
                </div>
                <div className={`text-sm font-medium px-2 py-1 rounded ${card.changeColor} bg-white`}>
                  {card.change}
                </div>
              </div>

              <div className="mb-2">
                <h3 className="text-sm font-medium text-gray-600 mb-1">{card.title}</h3>
                <p className="text-3xl font-bold text-gray-900">
                  {formatCurrency(card.value)}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {formatNumber(card.value)} {t('feeChargeCards.kip')}
                </p>
              </div>

              <div className="flex items-center text-sm text-gray-600">
                <TrendingUp className="h-4 w-4 mr-1" />
                {card.description}
              </div>

              {/* Progress Bar for collection rate */}
              {index === 1 && (
                <div className="mt-4">
                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                    <span>{t('feeChargeCards.collectionProgress')}</span>
                    <span>{feeData.collectionRate}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${feeData.collectionRate}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Detailed Breakdown */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">{t('feeChargeCards.weeklyBreakdown')}</h3>
        {weeklyData.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {weeklyData.map((week, index) => {
              const weekTotal = week.collected;
              const weeklyTarget = feeData.totalEstimate / 4; // Assume equal distribution
              const weeklyRate = weeklyTarget > 0 ? (weekTotal / weeklyTarget) * 100 : 0;

              const getWeeklyStatusColor = (rate) => {
                if (rate >= 90) return 'text-green-600';
                if (rate >= 70) return 'text-orange-600';
                return 'text-red-600';
              };

              return (
                <div key={week.week} className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900">
                    {formatCurrency(weekTotal)}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">{t('feeChargeCards.week', { value: week.week })}</div>
                  <div className={`text-xs font-medium mt-1 ${getWeeklyStatusColor(weeklyRate)}`}>
                    {t('feeChargeCards.ofTarget', { value: weeklyRate.toFixed(0) })}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {t('feeChargeCards.transactions', { value: week.transactionCount })}
                  </div>
                </div>
              );
            })}
            {/* Fill remaining weeks if less than 4 weeks of data */}
            {Array.from({ length: Math.max(0, 4 - weeklyData.length) }, (_, index) => (
              <div key={`empty-week-${index}`} className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-400">
                  {formatCurrency(0)}
                </div>
                <div className="text-sm text-gray-600 mt-1">{t('feeChargeCards.week', { value: weeklyData.length + index + 1 })}</div>
                <div className="text-xs text-gray-400 font-medium mt-1">{t('feeChargeCards.noDataShort')}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">{t('feeChargeCards.noWeeklyData')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeeChargeCards;